import { useState, useRef, useEffect } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import {
  colors,
  useMediaQuery,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Avatar,
  Typography,
  Grid,
  Divider,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { LoadingButton } from '@material-ui/lab';

import PropTypes from 'prop-types';
import useSWR from 'swr';

import { Icon, Image, LearnMoreLink, CircularProgressWithLabel } from '@/src/components/atoms';
import { CardProduct } from '@/src/components/organisms';
import { scroller } from '@/src/lib/utils';

const useStyles = makeStyles(theme => ({
  pagePaddingTop: {
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(5),
    },
  },
  sectionAlternate: {
    backgroundColor: theme.palette.background.default,
    '& .section-alternate__content': {
      padding: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(5),
      },
    },
  },
  searchInputContainer: {
    background: theme.palette.alternate.main,
    padding: theme.spacing(1),
    boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.11)',
    borderRadius: theme.spacing(1),
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    '& .MuiOutlinedInput-notchedOutline': {
      border: '0 !important',
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: theme.spacing(2),
    },
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: 0,
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  searchButton: {
    maxHeight: 45,
    minWidth: 135,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  cardProduct: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: theme.spacing(1),
    '& .card-product__content': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
  image: {
    objectFit: 'cover',
    borderRadius: theme.spacing(0, 0, 10, 0),
  },
  blogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  list: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  answerCount: {
    padding: theme.spacing(1 / 2, 1),
    borderRadius: theme.spacing(1),
    background: theme.palette.secondary.light,
    color: 'white',
    fontWeight: 500,
  },
  cardMediaClassName: {
    height: 150,
  },
  cta: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const Find = props => {
  const { className, ...rest } = props;

  const classes = useStyles();
  const router = useRouter();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
  const resultsRef = useRef();
  const [input, setInput] = useState('');

  const { query } = router;
  const { search = '' } = query;

  useEffect(() => {
    setInput(search);
  }, [search]);

  const { data, isValidating } = useSWR(search ? `/commons/institutions/?search=${search}` : null, {
    dedupingInterval: 3000,
  });

  const scrollToRef = ref => setTimeout(() => scroller(ref.current.offsetTop - 200), 500);

  const handleFind = () => {
    router.replace({ pathname: '/', query: { search: input } }, undefined, {
      scroll: false,
    });

    scrollToRef(resultsRef);
  };

  const MarketplaceMediaContent = content => (
    <Image {...content} className={classes.image} lazyProps={{ width: '100%', height: '100%' }} />
  );

  const MarketplaceContent = content => (
    <div className={classes.blogContent}>
      <div className={classes.list}>
        <div className={classes.avatarContainer}>
          <Avatar src={content.logo} className={classes.avatar} />
          <Typography variant="body1" color="textPrimary" gutterBottom>
            {content.title}
          </Typography>
        </div>
      </div>
      <Divider className={classes.divider} />
      <Typography variant="body1" color="textSecondary">
        {content.subtitle}
      </Typography>
      <div style={{ flexGrow: 1 }} />
      <div className={classes.cta}>
        <Link href={content.href} passHref>
          <LearnMoreLink
            title="entrar na loja"
            typographyProps={{ variant: 'h6', component: 'div' }}
          />
        </Link>
      </div>
    </div>
  );

  return (
    <div className={className} {...rest}>
      <div className={classes.pagePaddingTop}>
        <div className={classes.searchInputContainer}>
          <FormControl fullWidth variant="outlined">
            <OutlinedInput
              startAdornment={
                <InputAdornment position="start">
                  <Icon fontIconClass="fas fa-search" fontIconColor={colors.blueGrey[900]} />
                </InputAdornment>
              }
              onKeyPress={ev => {
                if (ev.key === 'Enter') {
                  handleFind();
                  ev.preventDefault();
                }
              }}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Encontre uma loja"
            />
          </FormControl>
          <LoadingButton
            loading={!!isValidating}
            loadingPosition="end"
            endIcon={<SearchIcon />}
            color="primary"
            variant="contained"
            size="large"
            className={classes.searchButton}
            onClick={handleFind}>
            Buscar
          </LoadingButton>
        </div>
      </div>
      <div ref={resultsRef}>
        {isValidating ? (
          <center>
            <CircularProgressWithLabel size={90} />
          </center>
        ) : (
          data && (
            <div className={classes.sectionAlternate}>
              <Grid container spacing={isMd ? 4 : 2}>
                <Grid item xs={12} container>
                  <Typography variant="body1" className={classes.answerCount}>
                    {data?.count} resultado(s) encontrado(s)
                  </Typography>
                </Grid>
                {data.results.map((item, index) => (
                  <Grid item md={3} sm={4} xs={12} key={index.toString()}>
                    <CardProduct
                      withShadow
                      liftUp
                      className={classes.cardProduct}
                      mediaClassName={classes.cardMediaClassName}
                      mediaContent={
                        <MarketplaceMediaContent src={item.cover} alt={item.displayName} />
                      }
                      cardContent={
                        <MarketplaceContent
                          title={item.displayName}
                          subtitle={item.address}
                          logo={item.logo}
                          href={`/e/${item.slug}`}
                        />
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </div>
          )
        )}
      </div>
    </div>
  );
};

Find.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
};

export default Find;
